import { ActivityDescriptionPopover } from "@/components/ActivityDescriptionPopover/ActivityDescriptionPopover";
import styles from "./ActivityTitle.module.scss";
import { ActivityIsHiddenPopover } from "@/components/ActivityIsHiddenPopover/ActivityIsHiddenPopover";
import RecurrenceIcon from "@medoma/components/icons/RecurrenceIcon";
import {
  categorySchema,
  type ISharedActivityOccurrenceFields,
} from "@medoma/models/activities";
import { Link } from "@medoma/components/Link/Link";
import type { IText } from "@medoma/components/Text/Text";
import { Text, TextWithLineBreaks } from "@medoma/components/Text/Text";
import { ActivityOccurrenceCommentPopover } from "@/components/ActivityOccurrenceCommentPopover/ActivityOccurrenceCommentPopover";
import clsx from "clsx";
import type { ReactNode } from "react";

const Title = ({
  title,
  linkTo,
  weight,
  size,
}: {
  title: string;
  linkTo?: string;
  weight?: IText["weight"];
  size: IText["size"];
}) =>
  linkTo ? (
    <Link to={linkTo} weight={weight} size={size} color="neutral">
      {title}
    </Link>
  ) : (
    <Text element="span" weight={weight} size={size} color="default">
      {title}
    </Text>
  );

// Accepts every kind of activity occurrence, so it takes the fields they all
// share rather than the union — callers pass occurrences assembled from several
// sources, not always one whole variant.
type IActivityTitle = {
  activityOccurrence: ISharedActivityOccurrenceFields;
  linkTo?: string;
  statusTag?: ReactNode;
  showDescriptionAndComment: boolean;
  showRecurringIcon: boolean;
  endOfTitleRowSlot?: React.ReactNode;
} & Pick<IText, "weight" | "size">;

export const ActivityTitle = ({
  activityOccurrence,
  linkTo,
  weight,
  size = "base",
  statusTag,
  showDescriptionAndComment,
  showRecurringIcon,
  endOfTitleRowSlot,
}: IActivityTitle) => {
  const {
    activityId,
    category,
    description,
    hidden: isHidden,
    recurring: isRecurring,
    title,
    note,
  } = activityOccurrence;

  const noteAndDescription =
    !note && !description
      ? undefined
      : [note, description].filter(Boolean).join("\n");

  if (showDescriptionAndComment) {
    return (
      <div className={clsx(styles.wrapper, styles[`size-${size}`])}>
        {statusTag ? (
          <div className={styles.statusTagWrapper}>{statusTag}</div>
        ) : null}
        <div className={styles.showDescriptionAndComment}>
          <div className={styles.titleAndIcons}>
            <Title title={title} linkTo={linkTo} weight={weight} size={size} />
            {isRecurring && showRecurringIcon && <RecurrenceIcon />}
            {isHidden && category !== categorySchema.enum.AdminTask && (
              <ActivityIsHiddenPopover
                activityId={activityId}
                isRecurring={isRecurring}
              />
            )}
            {endOfTitleRowSlot}
          </div>
          <TextWithLineBreaks size={size} color="faded">
            {noteAndDescription}
          </TextWithLineBreaks>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {statusTag ? (
        <div className={styles.statusTagWrapper}>{statusTag}</div>
      ) : null}
      <div className={clsx(styles.titleAndIcons, styles[`size-${size}`])}>
        <Title title={title} linkTo={linkTo} weight={weight} size={size} />
        {note && <ActivityOccurrenceCommentPopover note={note} />}
        {description && (
          <ActivityDescriptionPopover description={description} />
        )}
        {isRecurring && showRecurringIcon && <RecurrenceIcon />}
        {isHidden && category !== categorySchema.enum.AdminTask && (
          <ActivityIsHiddenPopover
            activityId={activityId}
            isRecurring={isRecurring}
          />
        )}
        {endOfTitleRowSlot}
      </div>
    </div>
  );
};
